import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import {
  isAuthenticated,
  setCookie,
  API,
  initHeader,
  refreshLoggedInUser,
} from "../../utils/common";
import { RESPONSE, ROLE } from "../../utils/const";
import i18n from "../../utils/Language";
import { normalizeLang } from "../../utils/normalizeLang";

// shadcn/ui components
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Card, CardContent } from "../../components/ui/card";
import { ChevronDown, AlertCircle, Clock, Globe } from "lucide-react";
import PasswordVisibilityToggleIcon from "../../components/common/PasswordVisibilityToggleIcon";

// Assets
import Logo from "../../assets/images/logo-header.png";
import LogoName from "../../assets/images/mercury-wordmark.png";

// Map backend role names to frontend role IDs
const ROLE_NAME_TO_ID = {
  admin: ROLE.ICT_ROLE,
  ict: ROLE.ICT_ROLE,
  hr_manager: ROLE.HR_ROLE,
  hr_staff: ROLE.HR_ROLE,
  director: ROLE.DIRECTOR,
  head_of_factory: ROLE.HEAD_OF_FACTORY,
  department_manager: ROLE.MANAGER_ROLE,
  payroll_manager: ROLE.MANAGER_ROLE,
  sub_manager: ROLE.SUB_MANAGER_ROLE,
  team_lead: ROLE.LEADER_ROLE,
  sub_leader: ROLE.SUB_LEADER_ROLE,
  employee: ROLE.EMPLOYEE_ROLE,
};

const resolveRoleId = (roleNames = []) => {
  if (!Array.isArray(roleNames) || roleNames.length === 0) {
    return ROLE.EMPLOYEE_ROLE;
  }

  const roleIds = roleNames
    .map((roleName) => ROLE_NAME_TO_ID[roleName])
    .filter((roleId) => Number.isInteger(roleId));

  if (roleIds.length === 0) {
    return ROLE.EMPLOYEE_ROLE;
  }

  return Math.min(...roleIds);
};

const Login = ({ location }) => {
  const { t } = useTranslation(["common", "errors"]);

  const [formState, setFormState] = useState({
    // Store translation keys (not translated strings) so messages update when language changes.
    userName: { value: "", isValid: true, messageKey: "" },
    passWord: { value: "", isValid: true, messageKey: "" },
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [isShowNotification, setIsShowNotification] = useState(false);
  const [messageError, setMessageError] = useState("");
  const [messageErrorNs, setMessageErrorNs] = useState("common");
  const [messageErrorKey, setMessageErrorKey] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);
  const [isLogin, setIsLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const languageObj = {
    en: "ENG",
    ja: "JP",
    vi: "VI",
  };
  const [language, setLanguage] = useState(
    normalizeLang(i18n.language) || normalizeLang(localStorage.getItem("lang")) || "vi"
  );

  const validateUserName = (rawValue) => {
    const value = String(rawValue || "").trim();

    if (!value) {
      return { isValid: false, messageKey: "login.errorEmail" };
    }

    return { isValid: true, messageKey: "" };
  };

  const validatePassword = (rawValue) => {
    const value = String(rawValue || "");

    if (!value) {
      return { isValid: false, messageKey: "login.errorPass" };
    }

    return { isValid: true, messageKey: "" };
  };

  const handleSetLang = (lang) => {
    const langNormalized = normalizeLang(lang) || "en";
    localStorage.setItem("lang", langNormalized);
    i18n.changeLanguage(langNormalized).catch(() => {});
    setLanguage(langNormalized);
  };

  useEffect(() => {
    const syncLanguage = (lng) => {
      const normalized =
        normalizeLang(lng) || normalizeLang(localStorage.getItem("lang")) || "vi";
      setLanguage(normalized);
    };

    syncLanguage(i18n.resolvedLanguage || i18n.language);

    i18n.on("languageChanged", syncLanguage);
    return () => {
      i18n.off("languageChanged", syncLanguage);
    };
  }, []);

  const handleChange = (e) => {
    if (isDisabled) return;
    const { name, value } = e.target;
    setFormState((prev) => {
      const prevFieldState = prev[name];
      const shouldRevalidate =
        prevFieldState && (!prevFieldState.isValid || prevFieldState.messageKey);
      const validation =
        shouldRevalidate && name === "userName"
          ? validateUserName(value)
          : shouldRevalidate && name === "passWord"
            ? validatePassword(value)
            : null;

      return {
        ...prev,
        [name]: {
          ...prev[name],
          value: value,
          ...(validation ? validation : {}),
        },
      };
    });
    setIsShowNotification(false);
    setMessageError("");
    setMessageErrorNs("common");
    setMessageErrorKey("");
  };

  const handleBlur = (e) => {
    if (isDisabled) return;
    const { name, value } = e.target;

    const validation =
      name === "userName"
        ? validateUserName(value)
        : name === "passWord"
          ? validatePassword(value)
          : null;

    if (!validation) return;

    setFormState((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        ...validation,
      },
    }));
  };

  const handleKeyPress = (e) => {
    if (isDisabled) return;
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const formIsValid = () => {
    const userNameValidation = validateUserName(formState.userName.value);
    const passWordValidation = validatePassword(formState.passWord.value);

    setFormState((prev) => ({
      ...prev,
      userName: { ...prev.userName, ...userNameValidation },
      passWord: { ...prev.passWord, ...passWordValidation },
    }));

    return userNameValidation.isValid && passWordValidation.isValid;
  };

  const handleSubmit = async () => {
    if (isDisabled) return;
    if (formIsValid()) {
      setIsDisabled(true);
      const infos = {
        email: formState.userName.value.trim(),
        password: formState.passWord.value,
        remember_me: rememberMe ? 1 : 0,
      };

      try {
        const response = await API.post("auth/login", infos);
        if (response.status === RESPONSE.STATUS_SUCCESS) {
          // Backend returns: { user, token, token_type }
          // user.roles is an array of role names like ["ict"]
          const { token, user } = response.data.data;
          if (token) {
            // Map role name to numeric ID for frontend RBAC
            const roleId = resolveRoleId(user?.roles);
            const tokenExpires =
              response.data?.data?.expires_at ||
              response.data?.data?.token_expires_at ||
              response.data?.data?.expires_in ||
              undefined;
            const cookieExpires = rememberMe ? tokenExpires : "session";

            setCookie(token, cookieExpires);
            setCookie(roleId, cookieExpires, "roles");
            initHeader();

            // Rehydrate current user + permissions before redirecting to protected routes.
            // This prevents a brief permission-check race that can redirect to /error400 right after login.
            try {
              await refreshLoggedInUser();
            } catch (e) {
              // If profile refresh fails, proceed with login; route guards may still deny if permissions are unknown.
            }

            setIsLogin(true);
          }
        }
      } catch (error) {
        // Prefer backend error codes so the UI can re-translate when language changes.
        // Backend shape (Api error): { message, error: { code, ... }, error.fields: { email: { code }, ... } }
        const apiError = error?.response?.data?.error || error?.error || null;
        const errorCode = apiError?.code || "";
        const fieldCode =
          apiError?.fields?.email?.code ||
          apiError?.fields?.password?.code ||
          apiError?.fields?.general?.code ||
          "";
        const i18nErrorKey =
          (typeof fieldCode === "string" && fieldCode) ||
          (typeof errorCode === "string" && errorCode) ||
          "";

        if (i18nErrorKey) {
          setIsShowNotification(true);
          setMessageError("");
          setMessageErrorNs("errors");
          setMessageErrorKey(i18nErrorKey);
          setIsDisabled(false);
          return;
        }

        const errorMsg =
          (typeof error === "string" && error) ||
          error?.message ||
          error?.error ||
          error?.errors?.email?.[0] ||
          error?.errors?.password?.[0] ||
          "";

        setIsShowNotification(true);
        setMessageError(errorMsg);
        setMessageErrorNs(errorMsg ? "" : "common");
        setMessageErrorKey(errorMsg ? "" : "login.errorLogin");
        setIsDisabled(false);
      }
    }
  };

  // Redirect if already authenticated or just logged in
  // Check both React Router state and sessionStorage (from auto-logout redirect)
  const getRedirectPath = () => {
    // Priority 1: React Router state (from protected route redirects)
    if (location?.state?.from) {
      return location.state.from;
    }
    // Priority 2: sessionStorage (from auto-logout on 401)
    const savedPath = window.sessionStorage.getItem("redirectAfterLogin");
    if (savedPath) {
      window.sessionStorage.removeItem("redirectAfterLogin");
      return { pathname: savedPath };
    }
    // Default: home page
    return { pathname: "/" };
  };

  if (isAuthenticated() || isLogin) {
    return <Redirect to={getRedirectPath()} />;
  }

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-4xl">
        <div className="flex flex-col gap-6">
          <Card className="overflow-hidden p-0">
            <CardContent className="grid p-0 md:grid-cols-2">
              {/* Login Form */}
              <form
                className="p-6 md:p-8"
                onSubmit={(e) => e.preventDefault()}
                noValidate
              >
                <div className="flex flex-col gap-6">
                  {/* Header */}
                  <div className="flex flex-col items-center gap-2 text-center">
                    <div className="flex items-center gap-2">
                      <Clock className="h-8 w-8 text-primary" />
                      <h1 className="text-2xl font-bold">
                        {t("login.brandName") || "Mercury TNA"}
                      </h1>
                    </div>
                    <p className="text-muted-foreground text-balance text-sm">
                      {t("login.welcome") || "Time & Attendance Management System"}
                    </p>
                  </div>

                  {/* Error Alert */}
                  {isShowNotification && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        {messageErrorKey
                          ? t(messageErrorKey, { ns: messageErrorNs })
                          : messageError || t("login.errorLogin")}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Username / Email Field */}
                  <div className="space-y-2">
                    <Label htmlFor="email">
                      {t("login.usernameOrEmail") || "Username or Email"}
                    </Label>
                    <Input
                      id="email"
                      type="text"
                      name="userName"
                      placeholder={
                        t("login.usernameOrEmailPlaceholder") ||
                        t("login.emailPlaceholder") ||
                        "Your ID"
                      }
                      value={formState.userName.value}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      onKeyPress={handleKeyPress}
                      disabled={isDisabled}
                      className={cn(
                        !formState.userName.isValid && "border-destructive"
                      )}
                      required
                    />
                    {!formState.userName.isValid && formState.userName.messageKey && (
                      <p className="text-sm text-destructive">
                        {formState.userName.messageKey ? t(formState.userName.messageKey) : ""}
                      </p>
                    )}
                  </div>

                  {/* Password Field */}
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Label htmlFor="password">{t("login.password") || "Password"}</Label>
                      <button
                        type="button"
                        className="ml-auto text-sm underline-offset-2 hover:underline text-primary"
                      >
                        {t("login.resetPasword") || "Forgot password?"}
                      </button>
                    </div>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        name="passWord"
                        placeholder={t("login.passwordPlaceholder") || "Vui lòng nhập mật khẩu"}
                        value={formState.passWord.value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onKeyPress={handleKeyPress}
                        disabled={isDisabled}
                        className={cn(
                          !formState.passWord.isValid &&
                            "border-destructive",
                          "pr-10"
                        )}
                        required
                      />
                      <button
                        type="button"
                        disabled={isDisabled}
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label={
                          showPassword
                            ? t("login.hidePassword") || "Hide password"
                            : t("login.showPassword") || "Show password"
                        }
                      >
                        <PasswordVisibilityToggleIcon
                          isPasswordVisible={showPassword}
                          className="h-4 w-4"
                        />
                      </button>
                    </div>
                    {!formState.passWord.isValid && formState.passWord.messageKey && (
                      <p className="text-sm text-destructive">
                        {formState.passWord.messageKey ? t(formState.passWord.messageKey) : ""}
                      </p>
                    )}
                  </div>

                  {/* Remember Me */}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="remember"
                      checked={rememberMe}
                      disabled={isDisabled}
                      onCheckedChange={setRememberMe}
                    />
                    <Label
                      htmlFor="remember"
                      className="text-sm font-normal cursor-pointer"
                    >
                      {t("login.checkbox") || "Remember me"}
                    </Label>
                  </div>

                  {/* Login Button */}
                  <Button
                    type="submit"
                    onClick={handleSubmit}
                    disabled={isDisabled}
                    className="w-full"
                  >
                    {isDisabled ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        {t("login.loading") || "Loading..."}
                      </span>
                    ) : (
                      t("login.login") || "Login"
                    )}
                  </Button>

                  {/* Language Selector */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">
                          {t("login.selectLanguage") || "Select Language"}
                        </span>
                    </div>
                  </div>

                  <div className="flex justify-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="gap-2" disabled={isDisabled}>
                          <Globe className="h-4 w-4" />
                          {languageObj[language]}
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem
                          onSelect={(e) => {
                            e.preventDefault();
                            handleSetLang("en");
                          }}
                        >
                          {t("login.languageEnglish") || "English (ENG)"}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={(e) => {
                            e.preventDefault();
                            handleSetLang("ja");
                          }}
                        >
                          {t("login.languageJapanese") || "Japanese (JP)"}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={(e) => {
                            e.preventDefault();
                            handleSetLang("vi");
                          }}
                        >
                          {t("login.languageVietnamese") || "Vietnamese (VI)"}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </form>

              {/* Side Image */}
              <div className="bg-primary relative hidden md:block">
                <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-primary-foreground">
                  <img
                    src={Logo}
                    alt="Mercury Solution"
                    className="h-24 w-auto object-contain mb-6"
                  />
                  <img
                    src={LogoName}
                    alt="Mercury Solution"
                    className="h-16 w-auto object-contain mb-8 bg-white p-2 rounded"
                  />
                  <h2 className="text-2xl font-bold mb-2">
                    {t("login.brandVersion") || "Mercury TNA v2.0.23"}
                  </h2>
                  <p className="text-center text-primary-foreground/80">
                    {t("login.welcome") || "Time & Attendance Management System"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <p className="px-6 text-center text-xs text-muted-foreground">
            {t("login.footer", { year: new Date().getFullYear() }) ||
              "Copyright 2025 Mercury Solution. All rights reserved."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
